import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatMoney, formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Cell, LineChart, Line, Legend,
} from "recharts";
import {
  TrendingUp, Flame, Users, Package, BarChart3, CalendarDays, Trophy,
  ChevronDown, ChevronUp, Search,
} from "lucide-react";

const today = new Date();
const isoMonth = (d) => d.toISOString().slice(0, 7);
const isoDate = (d) => d.toISOString().slice(0, 10);

const RANGES = [
  { key: "1m",  label: "This month", from: () => { const d = new Date(today.getFullYear(), today.getMonth(), 1); return isoDate(d); } },
  { key: "3m",  label: "Last 3 mo",  from: () => { const d = new Date(today.getFullYear(), today.getMonth() - 2, 1); return isoDate(d); } },
  { key: "6m",  label: "Last 6 mo",  from: () => { const d = new Date(today.getFullYear(), today.getMonth() - 5, 1); return isoDate(d); } },
  { key: "12m", label: "Last 12 mo", from: () => { const d = new Date(today.getFullYear(), today.getMonth() - 11, 1); return isoDate(d); } },
  { key: "ytd", label: "YTD",        from: () => isoDate(new Date(today.getFullYear(), 0, 1)) },
];

function periodLabel(p) {
  if (!p) return "";
  if (p.includes("-W")) return p.replace("-W", " · W");
  const [y, m] = p.split("-");
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleDateString("en-IN", { month: "short", year: "2-digit" });
}

export default function PerformanceDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rangeKey, setRangeKey] = useState("6m");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState(isoDate(today));
  const [periodMode, setPeriodMode] = useState("month"); // month | week
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState({}); // party_id -> bool
  const [falling, setFalling] = useState(null);
  const [fallingOpen, setFallingOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/performance/falling-customers");
        setFalling(data);
      } catch (e) { /* silent */ }
    })();
  }, []);

  useEffect(() => {
    const sel = RANGES.find((r) => r.key === rangeKey);
    if (sel) setFromDate(sel.from());
  }, [rangeKey]);

  const load = async () => {
    if (!fromDate) return;
    setLoading(true);
    try {
      const { data } = await api.get("/performance/sales", {
        params: { from_date: fromDate, to_date: toDate, limit: 500 },
      });
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [fromDate, toDate]);

  const overall = data?.overall;
  const series = periodMode === "month" ? overall?.by_month || [] : overall?.by_week || [];
  const customers = useMemo(() => {
    const list = data?.customers || [];
    const q = search.trim().toLowerCase();
    return q ? list.filter((c) => c.party_name.toLowerCase().includes(q)) : list;
  }, [data, search]);

  const topByKg = (customers || []).slice(0, 10).map((c) => ({
    name: c.party_name.length > 20 ? c.party_name.slice(0, 18) + "…" : c.party_name,
    kg: c.total_kg,
  }));

  const toggleExpand = (pid) => setExpanded((m) => ({ ...m, [pid]: !m[pid] }));

  return (
    <div className="space-y-6" data-testid="performance-dashboard">
      {falling && falling.total_falling > 0 && (
        <div className="card-flat p-4 border-2 border-[#B91C1C] bg-[#FEE2E2]" data-testid="falling-banner">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="small-label text-[#B91C1C] tracking-widest">Falling customers — action required</div>
              <div className="font-heading font-black text-xl text-[#B91C1C] mt-0.5" data-testid="falling-count">
                {falling.total_falling} customer{falling.total_falling === 1 ? "" : "s"} projected to drop &gt; {falling.threshold_pct}% this month
              </div>
              <div className="text-[11px] text-[#B91C1C]/80 mt-1">
                Baseline = avg of {(falling.baseline_months || []).join(", ")} · projected from {falling.days_elapsed}/{falling.days_in_month} completed days this month
              </div>
            </div>
            <button onClick={() => setFallingOpen((v) => !v)}
              className="btn-primary bg-[#B91C1C] border-[#B91C1C] hover:bg-black h-9 px-4 text-xs"
              data-testid="falling-toggle">
              {fallingOpen ? "Hide list" : "Show falling list"}
            </button>
          </div>
          {fallingOpen && (
            <div className="mt-4 table-wrap">
              <table className="table-dense w-full">
                <thead>
                  <tr>
                    <th className="text-left">Customer</th>
                    <th className="num">Baseline kg/mo</th>
                    <th className="num">Projected kg</th>
                    <th className="num">Drop %</th>
                    {(falling.baseline_months || []).map((m) => (
                      <th key={m} className="num text-[10px]">{m.split("-")[1]}</th>
                    ))}
                    <th className="num text-[10px]">Now</th>
                  </tr>
                </thead>
                <tbody>
                  {falling.falling.map((r) => (
                    <tr key={r.party_id} data-testid={`falling-row-${r.party_id}`}>
                      <td>
                        <Link to={`/parties/${r.party_id}`} className="font-semibold hover:underline">{r.party_name}</Link>
                        {r.phone && <div className="text-[10px] text-gray-500">{r.phone}</div>}
                      </td>
                      <td className="num data-num">{formatMoney(r.baseline_monthly_kg)}</td>
                      <td className="num data-num text-[#B91C1C] font-bold">{formatMoney(r.projected_month_kg)}</td>
                      <td className="num data-num text-[#B91C1C] font-black">-{r.drop_pct.toFixed(1)}%</td>
                      {(falling.baseline_months || []).map((m) => (
                        <td key={m} className="num data-num text-[10px] text-gray-500">{formatMoney(r.months[m] || 0)}</td>
                      ))}
                      <td className="num data-num text-[10px] text-[#B91C1C] font-bold">{formatMoney(r.months[falling.this_month] || 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Trophy size={20} className="text-[#B45309]" />
          <h2 className="font-heading font-black text-2xl tracking-tight">Customer Performance</h2>
        </div>
        <span className="text-xs text-gray-500 border border-gray-200 rounded-sm px-2 py-0.5">
          LPG sold per customer · by month or week
        </span>
      </div>

      {/* Range controls */}
      <div className="card-flat p-4 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          {RANGES.map((r) => (
            <button key={r.key} onClick={() => setRangeKey(r.key)}
              className={`px-3 h-9 rounded-sm text-xs font-semibold border ${rangeKey === r.key ? "bg-black text-white border-black" : "bg-white text-gray-600 border-gray-300 hover:border-black"}`}
              data-testid={`range-${r.key}`}>
              {r.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 ml-2 text-xs text-gray-600">
          <CalendarDays size={14} />
          <input type="date" className="input-lg h-9" value={fromDate}
            onChange={(e) => setFromDate(e.target.value)} data-testid="perf-from" />
          <span>→</span>
          <input type="date" className="input-lg h-9" value={toDate}
            onChange={(e) => setToDate(e.target.value)} data-testid="perf-to" />
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button onClick={() => setPeriodMode("month")}
            className={`px-3 h-9 rounded-sm text-xs font-semibold border ${periodMode === "month" ? "bg-black text-white border-black" : "bg-white text-gray-600 border-gray-300 hover:border-black"}`}
            data-testid="period-month">
            Monthly
          </button>
          <button onClick={() => setPeriodMode("week")}
            className={`px-3 h-9 rounded-sm text-xs font-semibold border ${periodMode === "week" ? "bg-black text-white border-black" : "bg-white text-gray-600 border-gray-300 hover:border-black"}`}
            data-testid="period-week">
            Weekly
          </button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="card-flat p-5 bg-black text-white border-black">
          <div className="small-label text-white/70 flex items-center gap-1.5"><Flame size={12} /> Total LPG sold</div>
          <div className="data-num text-3xl mt-1" data-testid="perf-total-kg">
            {formatMoney(overall?.total_kg || 0)} <span className="text-base font-medium text-white/70">KG</span>
          </div>
          <div className="text-xs text-white/70 mt-1">{periodMode === "month" ? "monthly view" : "weekly view"}</div>
        </div>
        <div className="card-flat p-5">
          <div className="small-label text-gray-500 flex items-center gap-1.5"><TrendingUp size={12} /> Revenue</div>
          <div className="data-num text-3xl mt-1" data-testid="perf-total-amount">₹ {formatMoney(overall?.total_amount || 0)}</div>
        </div>
        <div className="card-flat p-5">
          <div className="small-label text-gray-500 flex items-center gap-1.5"><Package size={12} /> Orders</div>
          <div className="data-num text-3xl mt-1" data-testid="perf-orders">{overall?.n_orders || 0}</div>
        </div>
        <div className="card-flat p-5">
          <div className="small-label text-gray-500 flex items-center gap-1.5"><Users size={12} /> Active customers</div>
          <div className="data-num text-3xl mt-1" data-testid="perf-active">{overall?.active_customers || 0}</div>
        </div>
        <div className="card-flat p-5">
          <div className="small-label text-gray-500 flex items-center gap-1.5"><BarChart3 size={12} /> Avg KG/order</div>
          <div className="data-num text-3xl mt-1" data-testid="perf-avg-kg">{formatMoney(overall?.avg_kg_per_order || 0)}</div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card-flat p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="small-label text-gray-500">{periodMode === "month" ? "Monthly" : "Weekly"} LPG sold</div>
              <h3 className="font-heading font-black text-lg">KG over time</h3>
            </div>
            <Flame size={18} className="text-[#B45309]" />
          </div>
          <div style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer>
              <LineChart data={series} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="2 4" stroke="#eee" />
                <XAxis dataKey="period" tickFormatter={periodLabel} tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip
                  formatter={(v) => [`${formatMoney(v)} KG`, "Sold"]}
                  labelFormatter={(p) => periodLabel(p)}
                  contentStyle={{ borderRadius: 0, border: "1px solid #000", fontSize: 12 }}
                />
                <Line type="monotone" dataKey="kg" stroke="#B45309" strokeWidth={2.5} dot={{ r: 3, fill: "#B45309" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card-flat p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="small-label text-gray-500">Top customers</div>
              <h3 className="font-heading font-black text-lg">By KG sold</h3>
            </div>
            <Trophy size={18} className="text-[#B45309]" />
          </div>
          <div style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer>
              <BarChart data={topByKg} layout="vertical" margin={{ top: 0, right: 16, left: 30, bottom: 0 }}>
                <CartesianGrid strokeDasharray="2 4" stroke="#eee" />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={130} />
                <Tooltip formatter={(v) => [`${formatMoney(v)} KG`, "Sold"]} contentStyle={{ borderRadius: 0, border: "1px solid #000", fontSize: 12 }} />
                <Bar dataKey="kg" radius={[0, 2, 2, 0]}>
                  {topByKg.map((_, i) => (
                    <Cell key={i} fill={i === 0 ? "#000" : "#B45309"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Customer table */}
      <div className="card-flat">
        <div className="p-4 border-b border-gray-200 flex items-center gap-3 flex-wrap">
          <div className="small-label text-gray-500">Customer performance</div>
          <div className="ml-auto relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input className="input-lg h-9 w-56 pl-8" placeholder="Search customer…"
              value={search} onChange={(e) => setSearch(e.target.value)}
              data-testid="perf-search" />
          </div>
        </div>
        <div className="overflow-auto">
          <table className="table-dense w-full">
            <thead>
              <tr>
                <th className="text-left">#</th>
                <th className="text-left">Customer</th>
                <th className="num">Total KG</th>
                <th className="num">Revenue ₹</th>
                <th className="num">Orders</th>
                <th className="num">Avg KG/order</th>
                <th className="text-left">First order</th>
                <th className="text-left">Last order</th>
                <th className="text-right"></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan="9" className="py-8 text-center text-sm text-gray-400">Crunching sales data…</td></tr>}
              {!loading && customers.length === 0 && (
                <tr><td colSpan="9" className="py-8 text-center text-sm text-gray-400">No sales in this period.</td></tr>
              )}
              {!loading && customers.map((c, i) => {
                const breakdown = periodMode === "month" ? c.by_month : c.by_week;
                const open = !!expanded[c.party_id];
                return (
                  <React.Fragment key={c.party_id}>
                    <tr data-testid={`perf-row-${c.party_id}`} className={i === 0 ? "bg-[#FFF7ED]" : ""}>
                      <td className="text-sm font-bold">{i + 1}</td>
                      <td>
                        <Link to={`/parties/${c.party_id}`} className="font-semibold hover:underline">{c.party_name}</Link>
                        {c.phone && <div className="text-xs text-gray-500">{c.phone}</div>}
                      </td>
                      <td className="num font-black text-[#B45309]">{formatMoney(c.total_kg)}</td>
                      <td className="num">{formatMoney(c.total_amount)}</td>
                      <td className="num">{c.n_orders}</td>
                      <td className="num">{formatMoney(c.avg_kg_per_order)}</td>
                      <td className="data-num text-xs">{c.first_order}</td>
                      <td className="data-num text-xs">{c.last_order}</td>
                      <td className="text-right">
                        <button onClick={() => toggleExpand(c.party_id)}
                          className="text-xs text-gray-500 hover:text-black inline-flex items-center gap-1"
                          data-testid={`perf-expand-${c.party_id}`}>
                          {open ? <>Hide <ChevronUp size={13} /></> : <>Breakdown <ChevronDown size={13} /></>}
                        </button>
                      </td>
                    </tr>
                    {open && (
                      <tr>
                        <td></td>
                        <td colSpan="8" className="py-3 px-2 bg-gray-50">
                          <div className="text-xs font-semibold text-gray-500 mb-2">{periodMode === "month" ? "By month" : "By week"}</div>
                          <div style={{ width: "100%", height: 140 }}>
                            <ResponsiveContainer>
                              <BarChart data={breakdown} margin={{ top: 4, right: 12, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="2 4" stroke="#eee" />
                                <XAxis dataKey="period" tickFormatter={periodLabel} tick={{ fontSize: 10 }} />
                                <YAxis tick={{ fontSize: 10 }} />
                                <Tooltip formatter={(v) => [`${formatMoney(v)} KG`, "Sold"]} labelFormatter={(p) => periodLabel(p)} contentStyle={{ borderRadius: 0, border: "1px solid #000", fontSize: 11 }} />
                                <Legend wrapperStyle={{ fontSize: 11 }} />
                                <Bar dataKey="kg" name="KG" fill="#B45309" radius={[2, 2, 0, 0]} />
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
